import { useState } from "react";
import { X } from "lucide-react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import type { VideoStyle, VideoGenerationRequest } from "../types/videoStudio.types";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";
import { VideoStudioGallery } from "./VideoStudioGallery";
import { VideoStudioSidebar } from "./VideoStudioSidebar";
import { PreviewApproval } from "./PreviewApproval";
import useVideoGenerationStore from "@/store/videoGenerationStore";
import { useVideoDraftStore } from "@/store/videoDraftStore";
import { useVideoDraftFlag } from "../hooks/useVideoDraftFlag";

interface Step2VideoStudioProps {
  product: ProductInfoCreative;
  onClose: () => void;
}

export function Step2VideoStudio({ product, onClose }: Step2VideoStudioProps) {
  const [selectedStyle, setSelectedStyle] = useState<VideoStyle | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftMode, setDraftMode] = useState(false);
  const { language } = useAuth();
  const startVideoGeneration = useVideoGenerationStore((s) => s.startGeneration);
  const startDraft = useVideoDraftStore((s) => s.startDraft);
  const resetDraft = useVideoDraftStore((s) => s.reset);

  // Phase 2: feature flag — only enable draft preview for styles that have a
  // director_v1 agent created in agent-config (currently animated-problem).
  // For everything else we fall through to the legacy direct-render flow.
  const draftEnabled = useVideoDraftFlag(selectedStyle?.id);

  const handleSelectStyle = (style: VideoStyle) => {
    setSelectedStyle((prev) => (prev?.id === style.id ? null : style));
  };

  const handleClose = () => {
    if (draftMode) resetDraft();
    onClose();
  };

  const handleGenerate = async (partialPayload: VideoGenerationRequest) => {
    setIsGenerating(true);

    // ── New draft preview flow (Phase 2) ──
    if (draftEnabled && selectedStyle) {
      const ok = await startDraft({
        product_id: product.product_id,
        style_id: selectedStyle.id,
        duration: parseInt(partialPayload.duration || "30"),
        language: partialPayload.language || "es",
        sale_angle_name: partialPayload.angle,
        user_instruction: partialPayload.user_prompt,
      });
      setIsGenerating(false);
      if (ok) {
        setDraftMode(true);
      }
      return;
    }

    // ── Legacy flow (untouched) ──
    const success = await startVideoGeneration(
      {
        product_id: product.product_id,
        video_content_type: partialPayload.style_id || "UGC",
        style_id: partialPayload.style_id,
        language: partialPayload.language || "es",
        duration: parseInt(partialPayload.duration || "10"),
        tone: partialPayload.tone || undefined,
        instruction: partialPayload.user_prompt,
        angle: partialPayload.angle,
        problem_hint: partialPayload.problem_hint,
        avatar_config: partialPayload.avatar_config ? {
          gender: partialPayload.avatar_config.gender,
          age_range: partialPayload.avatar_config.age_range,
          skin_tone: partialPayload.avatar_config.skin_tone,
          hair: partialPayload.avatar_config.hair,
          vibe: partialPayload.avatar_config.vibe,
          setting: partialPayload.avatar_config.setting,
        } : undefined,
      },
      product.name
    );

    setIsGenerating(false);
    if (success) onClose();
  };

  // Phase 2: when draftMode is true the user already triggered the new draft
  // flow and should see the preview/approval screen instead of the studio.
  if (draftMode) {
    return (
      <PreviewApproval
        onBack={() => {
          resetDraft();
          setDraftMode(false);
        }}
        onDone={handleClose}
      />
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
      {/* Header — same pattern as AdStudioHeader */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-background border border-border overflow-hidden flex-shrink-0">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-contain p-0.5"
              />
            ) : (
              <div className="w-full h-full bg-muted/30" />
            )}
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground leading-tight">{dictionary["ad-studio-video"]?.[language as Language] || "Ad Studio Video"}</h2>
            <p className="text-sm text-muted-foreground leading-tight truncate max-w-[200px]">
              {product.name}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main content — 60/40 split */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-[60%] min-w-0 min-h-0 overflow-hidden flex-none">
          <VideoStudioGallery
            selectedStyleId={selectedStyle?.id ?? null}
            onSelectStyle={handleSelectStyle}
          />
        </div>

        <VideoStudioSidebar
          selectedStyle={selectedStyle}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
        />
      </div>
    </div>
  );
}
